import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, Loader2, Repeat, Search, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AppDialogBody,
  AppDialogContent,
  AppDialogFooter,
  appDialogPrimaryButton,
  appDialogSecondaryButton,
} from "@/components/ui/app-dialog";
import { getPreSaleRedoOptions, PreSaleRedoOption, redoPreSale } from "@/Api/preSales";
import { cn } from "@/lib/utils";
import { Mask } from "@/utils/mask";
import { PreSaleProduct } from "./PreSaleProductsList";

interface PreSaleRedoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preSale: {
    uuid: string;
    sale?: { id: number; total: string; products?: PreSaleProduct[] };
  } | null;
  /** Chamado depois que o Refazer foi gravado, para recarregar detalhes e lista */
  onDone: () => void | Promise<void>;
}

const toCents = (value: number | string | undefined | null) => Math.round(Number(value ?? 0) * 100);

/**
 * LOT-51: Refazer a pré-venda. O responsável troca (Trocar) um ou mais bolões por outros
 * disponíveis, desde que o valor final do pedido continue exatamente o mesmo. Não aprova: o pedido
 * fica aguardando o vendedor aceitar a nova composição.
 */
export function PreSaleRedoModal({ open, onOpenChange, preSale, onDone }: PreSaleRedoModalProps) {
  const [options, setOptions] = useState<PreSaleRedoOption[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  // classification id do item original -> bolão escolhido para entrar no lugar
  const [swaps, setSwaps] = useState<Record<number, PreSaleRedoOption>>({});
  // item original que está escolhendo o substituto (abre a lista)
  const [pickingFor, setPickingFor] = useState<PreSaleProduct | null>(null);
  const [search, setSearch] = useState("");
  const [notes, setNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const products = preSale?.sale?.products ?? [];

  useEffect(() => {
    if (!open || !preSale) return;

    setSwaps({});
    setPickingFor(null);
    setSearch("");
    setNotes("");
    setIsLoadingOptions(true);

    getPreSaleRedoOptions(preSale.uuid)
      .then(({ ok, data }) => {
        if (ok && Array.isArray(data?.response)) {
          setOptions(data.response);
        } else {
          // Motivo já exibido pelo interceptor da API; não empilhar um toast genérico por cima
          setOptions([]);
        }
      })
      .catch(() => {
        setOptions([]);
        toast.error("Erro ao buscar bolões disponíveis");
      })
      .finally(() => setIsLoadingOptions(false));
  }, [open, preSale?.uuid]);

  const originalCents = toCents(preSale?.sale?.total);

  const newCents = useMemo(
    () =>
      products.reduce((sum, product) => {
        const swap = swaps[product.id];
        const quantity = product.pivot?.quantity ?? 1;

        if (!swap) return sum + toCents(product.pivot?.price_total);

        return (
          sum +
          toCents(swap.contest_product_classification.product_classification?.sale_price) * quantity
        );
      }, 0),
    [products, swaps]
  );

  const swapCount = Object.keys(swaps).length;
  const sameValue = newCents === originalCents;
  const canConfirm = swapCount > 0 && sameValue && !isSaving;

  // Bolões já escolhidos em outro item não aparecem de novo na lista
  const pickedIds = useMemo(
    () =>
      new Set(
        Object.entries(swaps)
          .filter(([from]) => Number(from) !== pickingFor?.id)
          .map(([, option]) => option.cp_classification_id)
      ),
    [swaps, pickingFor?.id]
  );

  const filteredOptions = useMemo(() => {
    if (!pickingFor) return [];

    const quantity = pickingFor.pivot?.quantity ?? 1;
    const unitCents = toCents(pickingFor.pivot?.price_unit);
    const term = search.trim().toLowerCase();

    return options
      .filter((option) => !pickedIds.has(option.cp_classification_id))
      .filter((option) => option.available_quantity >= quantity)
      .filter((option) => {
        if (!term) return true;
        const info = option.contest_product_classification;
        return [info.product_name, info.lottery?.name, info.contest?.name]
          .filter(Boolean)
          .some((text) => String(text).toLowerCase().includes(term));
      })
      .sort((a, b) => {
        // mesmo valor do item original primeiro
        const sameA = toCents(a.contest_product_classification.product_classification?.sale_price) === unitCents;
        const sameB = toCents(b.contest_product_classification.product_classification?.sale_price) === unitCents;
        if (sameA !== sameB) return sameA ? -1 : 1;
        return a.contest_product_classification.product_name.localeCompare(
          b.contest_product_classification.product_name,
          "pt-BR",
          { numeric: true }
        );
      });
  }, [options, pickingFor, pickedIds, search]);

  const handleSelect = (option: PreSaleRedoOption) => {
    if (!pickingFor) return;
    setSwaps((prev) => ({ ...prev, [pickingFor.id]: option }));
    setPickingFor(null);
    setSearch("");
  };

  const handleClearSwap = (productId: number) => {
    setSwaps((prev) => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });
  };

  const handleConfirm = async () => {
    if (!preSale || !canConfirm) return;

    setIsSaving(true);
    try {
      const { ok } = await redoPreSale(preSale.uuid, {
        items: Object.entries(swaps).map(([from, option]) => ({
          from_classification_id: Number(from),
          to_classification_id: option.cp_classification_id,
        })),
        notes: notes.trim() || undefined,
      });

      if (ok) {
        toast.success("Pré-venda refeita. Aguardando o vendedor aceitar.");
        onOpenChange(false);
        await onDone();
      }
      // Em caso de erro o motivo já é exibido pelo interceptor da API (Api/index.ts);
      // um toast genérico aqui ficava por cima e escondia o motivo real.
    } catch (error: any) {
      toast.error(error?.message || "Erro ao refazer pré-venda");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !isSaving && onOpenChange(value)}>
      <AppDialogContent
        icon={Repeat}
        title="Refazer venda"
        description={
          pickingFor
            ? `Escolha o bolão que entra no lugar de ${pickingFor.product_name}`
            : "Troque bolões mantendo exatamente o mesmo valor final"
        }
        className="sm:max-w-2xl"
      >
        {pickingFor ? (
          <>
            <AppDialogBody>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  className={appDialogSecondaryButton}
                  onClick={() => {
                    setPickingFor(null);
                    setSearch("");
                  }}
                >
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  Voltar
                </Button>
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por bolão, loteria ou concurso"
                    className="pl-8"
                  />
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Valor do item original:{" "}
                <strong>{Mask.formatCurrency(Number(pickingFor.pivot?.price_unit ?? 0))}</strong>. Os de mesmo valor
                aparecem primeiro; outros valores só fecham se a soma do pedido não mudar.
              </p>

              {isLoadingOptions ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : filteredOptions.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  Nenhum bolão disponível para troca.
                </p>
              ) : (
                <div className="max-h-[45vh] space-y-1.5 overflow-y-auto pr-1">
                  {filteredOptions.map((option) => {
                    const info = option.contest_product_classification;
                    const price = Number(info.product_classification?.sale_price ?? 0);
                    const sameUnitPrice = toCents(price) === toCents(pickingFor.pivot?.price_unit);

                    return (
                      <button
                        key={option.cp_classification_id}
                        type="button"
                        onClick={() => handleSelect(option)}
                        className="flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-white transition hover:brightness-110"
                        style={{ backgroundColor: info.lottery?.color || "#64748b" }}
                      >
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xl font-bold leading-tight">{info.product_name}</span>
                            {info.lottery && (
                              <span className="rounded border border-white/30 bg-white/15 px-2 py-0.5 text-xs font-medium">
                                {info.lottery.name}
                              </span>
                            )}
                            {sameUnitPrice && (
                              <span className="rounded bg-white px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">
                                Mesmo valor
                              </span>
                            )}
                          </div>
                          <p className="truncate text-xs opacity-90">
                            {info.contest?.name} · disponível: {option.available_quantity}
                          </p>
                        </div>
                        <span className="shrink-0 text-sm font-semibold">{Mask.formatCurrency(price)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </AppDialogBody>
          </>
        ) : (
          <>
            <AppDialogBody>
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Produtos</h4>

                {products.map((product) => {
                  const swap = swaps[product.id];
                  const quantity = product.pivot?.quantity ?? 1;
                  const swapInfo = swap?.contest_product_classification;

                  return (
                    <div
                      key={product.id}
                      className="flex flex-col gap-2 sm:flex-row sm:items-center"
                    >
                      <div className="flex min-w-0 flex-1 flex-col gap-1.5 sm:flex-row sm:items-center">
                        <div
                          className={cn(
                            "flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md px-3 py-2 text-white",
                            swap && "opacity-60"
                          )}
                          style={{ backgroundColor: product.lottery?.color || "#64748b" }}
                        >
                          <div className="min-w-0">
                            <p className={cn("truncate text-xl font-bold leading-tight", swap && "line-through")}>
                              {quantity > 1 && `${quantity}x `}
                              {product.product_name}
                            </p>
                            {product.lottery && <p className="truncate text-xs opacity-90">{product.lottery.name}</p>}
                          </div>
                          <span className="shrink-0 text-sm font-medium">
                            {Mask.formatCurrency(Number(product.pivot?.price_total ?? 0))}
                          </span>
                        </div>

                        {swap && swapInfo && (
                          <>
                            <ArrowRight className="mx-auto h-4 w-4 shrink-0 rotate-90 text-muted-foreground sm:rotate-0" />
                            <div
                              className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md px-3 py-2 text-white"
                              style={{ backgroundColor: swapInfo.lottery?.color || "#64748b" }}
                            >
                              <div className="min-w-0">
                                <p className="truncate text-xl font-bold leading-tight">
                                  {quantity > 1 && `${quantity}x `}
                                  {swapInfo.product_name}
                                </p>
                                {swapInfo.lottery && (
                                  <p className="truncate text-xs opacity-90">{swapInfo.lottery.name}</p>
                                )}
                              </div>
                              <span className="shrink-0 text-sm font-medium">
                                {Mask.formatCurrency(
                                  Number(swapInfo.product_classification?.sale_price ?? 0) * quantity
                                )}
                              </span>
                            </div>
                          </>
                        )}
                      </div>

                      {swap ? (
                        <Button
                          type="button"
                          size="sm"
                          className={cn("sm:w-24", appDialogSecondaryButton)}
                          onClick={() => handleClearSwap(product.id)}
                        >
                          <Undo2 className="mr-1 h-4 w-4" />
                          Desfazer
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          className="bg-slate-500 text-white hover:bg-slate-600 sm:w-24"
                          onClick={() => setPickingFor(product)}
                          disabled={isLoadingOptions}
                        >
                          TROCAR
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>

              <div
                className={cn(
                  "flex flex-col gap-1 rounded-lg border p-3 text-sm sm:flex-row sm:items-center sm:justify-between",
                  swapCount === 0
                    ? "border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-gray-800"
                    : sameValue
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-200"
                      : "border-red-200 bg-red-50 text-red-800 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-200"
                )}
              >
                <span>
                  Valor original: <strong>{Mask.formatCurrency(originalCents / 100)}</strong>
                </span>
                <span>
                  Após a troca: <strong>{Mask.formatCurrency(newCents / 100)}</strong>
                </span>
                <span className="font-medium">
                  {swapCount === 0
                    ? "Nenhuma troca escolhida"
                    : sameValue
                      ? "Mesmo valor final"
                      : `Diferença de ${Mask.formatCurrency(Math.abs(newCents - originalCents) / 100)}`}
                </span>
              </div>

              {swapCount > 0 && !sameValue && (
                <p className="text-xs text-red-600 dark:text-red-400">
                  A troca só é permitida com o mesmo valor final. Ajuste os bolões ou, se não for possível,
                  rejeite a pré-venda.
                </p>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium">Observação</label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ex.: B142 sem estoque, trocado por outro de mesmo valor (opcional)"
                  maxLength={500}
                  rows={3}
                />
              </div>
            </AppDialogBody>

            <AppDialogFooter className="flex-col-reverse gap-2 sm:flex-row">
              <Button
                type="button"
                className={appDialogSecondaryButton}
                onClick={() => onOpenChange(false)}
                disabled={isSaving}
              >
                Cancelar
              </Button>
              <Button type="button" className={appDialogPrimaryButton} onClick={handleConfirm} disabled={!canConfirm}>
                {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Repeat className="mr-2 h-4 w-4" />}
                Refazer venda
              </Button>
            </AppDialogFooter>
          </>
        )}
      </AppDialogContent>
    </Dialog>
  );
}
